import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updateProviderInformation } from '@/app/apis/Providers/requests'
import { useToast } from '@/shad/ui/toast/use-toast'
import { parseRequestError } from '@/common/utilities'
import { VendorRequest } from '@/apis/Providers/types.ts'
import { ProviderQueryKeys } from '@/queries/providers/queries'

interface UseUpdateProviderInfoOptions {
  providerId: string
  programId: number
  onSuccess?: () => void
  onError?: (error: Error) => void
}

export const useUpdateProviderInfo = ({
  providerId,
  programId,
  onSuccess,
  onError,
}: UseUpdateProviderInfoOptions) => {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: VendorRequest) => {
      const enrichedPayload = {
        ...payload,
        program_id: programId,
      }
      return await updateProviderInformation(providerId, enrichedPayload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ProviderQueryKeys.details(programId, providerId),
      })
      toast({
        title: 'Account Owner Information Updated',
        description: 'Account owner information has been updated successfully.',
        variant: 'default',
      })
      onSuccess?.()
    },
    onError: (err: Error) => {
      const msg = parseRequestError(err).message
      toast({
        title: 'Account Owner Information Update Failed',
        description: msg,
        variant: 'destructive',
      })
      onError?.(err)
    },
  })
}
